import { formatMoneyExport } from '@nxsys/utils/number-helper';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment-timezone';
import { buildTableExport } from './tableExport';

// CIS keeps its own file layout.
const buildDraftCisExport = (
  data: DraftPayroll[],
  payrollCountry: PayrollCountryType,
): { headers: string[]; dataExport: Record<string, string>[] } => {
  const cs = showCurrencyByCountry(payrollCountry);
  const fm = (v: any) => (v != null ? formatMoneyExport(Number(v), cs) : '');
  const fd = (v: any) =>
    v ? moment(v).tz('Europe/London').format('DD/MM/YYYY') : '';
  const ref = (sub: any) =>
    `${sub?.departmentPrefix || sub?.primaryAgency?.agencyPrefix || '___'}/${sub?.agencyRef || ''}`;

  // CIS — giữ nguyên code cũ đã đúng
  const dataExport = data.map((item) => ({
    timeSheetID: item?.timeSheetRef?.id || '',
    batch: item?.timeSheetRef?.batch || '',
    subcontractorID: item?.subcontractorRef?.id || '',
    agencyReference: ref(item?.subcontractorRef),
    agencyName: item?.agencyName || '',
    subcontractorName: item?.subcontractorName || '',
    taxPeriod: String(item?.taxPeriod || ''),
    taxYear: String(item?.taxYear || ''),
    timeSheetValue: fm(item?.timesheetValue),
    hours: String(item?._totalHours || 0),
    margin: fm(item?.margin),
    gross: fm(item?.gross),
    materials: fm(item?.materials),
    vat: fm(item?.vat),
    net: fm(item?.net),
    cisStatus: item?.cisStatus || '',
    cisDeduction: fm(item?.cisDeduction),
    otherPayment: fm(item?._otherPayment),
    otherDeduction: fm(Math.abs(Number(item?._otherDeduction) || 0)),
    paymentDate: fd(item?.paymentDate),
    selfBillRef: item?.selfBillRef?.id || '',
    selfBillDate: fd(item?.selfBillRef?.billDate),
    salesInvRef: item?.invoiceNo || '',
  }));
  const headers = [
    'Timesheet ID',
    'Batch',
    'Employee ID',
    'Agency Reference',
    'Agency Name',
    'Employee Name',
    'Tax Period',
    'Tax Year',
    'Timesheet Values',
    'Hours',
    'Margin',
    'Gross',
    'Materials',
    'VAT',
    'Net',
    'CIS Status',
    'CIS Deduction (amount)',
    'Other Payment',
    'Other Deduction',
    'Payment Date',
    'Self Bill ID',
    'Self Bill Date',
    'Sales Inv Ref',
  ];

  return { headers, dataExport };
};

/**
 * The Draft Payroll CSV export, shared by the Draft Payroll report and the
 * dashboard Confirm Payroll modal.
 *
 * It is built from the columns the table is showing (buildTableExport), so the
 * file always has the same columns, names, order and figures as the screen.
 *
 * Returns the headers and rows; the caller owns the filename and ExportToCsv.
 */
export const buildDraftPayrollExport = ({
  data,
  columns,
  payrollCountry,
  isCisReportPage = false,
}: {
  data: DraftPayroll[];
  columns: ColumnsType<DraftPayroll>;
  payrollCountry: PayrollCountryType;
  isCisReportPage?: boolean;
}): { headers: string[]; dataExport: Record<string, string>[] } => {
  if (isCisReportPage) return buildDraftCisExport(data, payrollCountry);

  return buildTableExport(data, columns);
};
